/**
 * Unified Command Center - Landing Page After Sign-In
 * 
 * Single pane of glass for:
 * - Predictive Escalations (Pain, Dyspnea, PRN frequency, Decline)
 * - HIPAA Activity Monitor (Anomalies, Compliance Score)
 * - Call Triage Queue
 * - Unified Admission Dashboard (Single Source of Truth)
 * - Real-Time Operations Data
 * - AI Feature Status
 * 
 * Replaces separate Referral Dashboard - all referrals flow through Admissions
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  AlertTriangle, Bell, Activity, Users, Phone, Clock,
  CheckCircle, XCircle, TrendingUp, TrendingDown, Shield,
  Brain, Pill, Heart, Stethoscope, Building2, UserPlus,
  FileText, ClipboardCheck, Calendar, RefreshCw, Search,
  ChevronRight, Eye, AlertCircle, Zap, Target, Timer,
  BarChart3, PieChart, ArrowRight, Play, Pause, Home,
  Hospital, User, MapPin, DollarSign, FileCheck, Mic
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Status colors for workflow
const STATUS_COLORS = {
  new: { bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-500" },
  under_review: { bg: "bg-amber-100", text: "text-amber-800", border: "border-amber-500" },
  pending_documentation: { bg: "bg-orange-100", text: "text-orange-800", border: "border-orange-500" },
  pending_cti: { bg: "bg-purple-100", text: "text-purple-800", border: "border-purple-500" },
  approved: { bg: "bg-green-100", text: "text-green-800", border: "border-green-500" },
  scheduled_soc: { bg: "bg-teal-100", text: "text-teal-800", border: "border-teal-500" },
  admitted: { bg: "bg-emerald-100", text: "text-emerald-800", border: "border-emerald-500" },
  not_admitted: { bg: "bg-red-100", text: "text-red-800", border: "border-red-500" }
};

// Priority colors for escalations
const PRIORITY_COLORS = {
  critical: { bg: "bg-red-600", text: "text-white", pulse: true },
  high: { bg: "bg-orange-500", text: "text-white", pulse: false },
  moderate: { bg: "bg-amber-500", text: "text-white", pulse: false },
  low: { bg: "bg-blue-500", text: "text-white", pulse: false }
};

// Referral source icons
const SOURCE_ICONS = {
  family: User,
  hospital: Hospital,
  snf: Building2,
  alf: Building2,
  physician: Stethoscope,
  partner: Users,
  internal: ArrowRight,
  self: User
};

export default function UnifiedCommandCenter() {
  const { user, role } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === "dark";

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("alerts");

  // Data states
  const [escalations, setEscalations] = useState([]);
  const [escalationSummary, setEscalationSummary] = useState({});
  const [hipaaData, setHipaaData] = useState(null);
  const [cmsAuditScore, setCmsAuditScore] = useState(null);
  const [triageQueue, setTriageQueue] = useState([]);
  const [admissionPipeline, setAdmissionPipeline] = useState([]);
  const [admissionStats, setAdmissionStats] = useState(null);
  const [operationsData, setOperationsData] = useState(null);
  const [medicationQueue, setMedicationQueue] = useState([]);

  // Dialog states
  const [showNewReferralDialog, setShowNewReferralDialog] = useState(false);
  const [selectedEscalation, setSelectedEscalation] = useState(null);
  const [showEscalationDialog, setShowEscalationDialog] = useState(false);

  // New referral form
  const [newReferral, setNewReferral] = useState({
    source: "hospital",
    source_name: "",
    patient_name: "",
    patient_dob: "",
    primary_diagnosis: "",
    urgency: "routine",
    care_location: "home",
    payer_type: "medicare",
    referring_physician: "",
    contact_phone: ""
  });

  // Fetch all dashboard data
  const fetchDashboardData = useCallback(async () => {
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      // Parallel fetch all data
      const [
        escalationRes,
        hipaaRes,
        triageRes,
        admissionRes,
        medQueueRes,
        cmsAuditRes
      ] = await Promise.all([
        fetch(`${API}/api/predictive-escalations/dashboard`, { headers }).catch(() => null),
        fetch(`${API}/api/hipaa-monitor/dashboard`, { headers }).catch(() => null),
        fetch(`${API}/api/triage/queue`, { headers }).catch(() => null),
        fetch(`${API}/api/admissions/unified-dashboard`, { headers }).catch(() => null),
        fetch(`${API}/api/medication-workflow/physician/queue`).catch(() => null),
        fetch(`${API}/api/compliance-automation/audit-readiness-score`, { headers }).catch(() => null)
      ]);

      // Process escalations
      if (escalationRes?.ok) {
        const data = await escalationRes.json();
        setEscalations(data.escalations || []);
        setEscalationSummary(data.summary || {});
      }

      // Process HIPAA data
      if (hipaaRes?.ok) {
        const data = await hipaaRes.json();
        setHipaaData(data);
      }

      // Process CMS Audit Score
      if (cmsAuditRes?.ok) {
        const data = await cmsAuditRes.json();
        setCmsAuditScore(data);
      }

      // Process triage queue
      if (triageRes?.ok) {
        const data = await triageRes.json();
        setTriageQueue(data.queue || data.calls || []);
      }

      // Process admissions (unified dashboard)
      if (admissionRes?.ok) {
        const data = await admissionRes.json();
        setAdmissionPipeline(data.pipeline_stats || {});
        setAdmissionStats({
          ...data,
          pipeline_stats: data.pipeline_stats,
          admitted_today: data.summary?.admitted_today || 0
        });
      }

      // Process medication queue
      if (medQueueRes?.ok) {
        const data = await medQueueRes.json();
        setMedicationQueue(data.queue || []);
      }

    } catch (error) {
      console.error("Dashboard fetch error:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboardData();
  };

  // Handle escalation acknowledgment
  const handleAcknowledgeEscalation = async (escalationId) => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API}/api/predictive-escalations/${escalationId}/acknowledge`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success("Escalation acknowledged");
        fetchDashboardData();
      }
    } catch (error) {
      toast.error("Failed to acknowledge");
    }
  };

  // Handle new referral submission
  const handleSubmitReferral = async () => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API}/api/admissions/referrals`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newReferral,
          referral_source: newReferral.source,
          referral_source_name: newReferral.source_name,
          patient_first_name: newReferral.patient_name.split(" ")[0],
          patient_last_name: newReferral.patient_name.split(" ").slice(1).join(" ") || "",
          patient_dob: newReferral.patient_dob,
          primary_diagnosis: newReferral.primary_diagnosis,
          attending_physician_name: newReferral.referring_physician,
          patient_phone: newReferral.contact_phone
        })
      });

      if (res.ok) {
        toast.success("Referral submitted to Admission Pipeline");
        setShowNewReferralDialog(false);
        setNewReferral({
          source: "hospital",
          source_name: "",
          patient_name: "",
          patient_dob: "",
          primary_diagnosis: "",
          urgency: "routine",
          care_location: "home",
          payer_type: "medicare",
          referring_physician: "",
          contact_phone: ""
        });
        fetchDashboardData();
      } else {
        toast.error("Failed to submit referral");
      }
    } catch (error) {
      toast.error("Error submitting referral");
    }
  };

  // Count alerts needing attention
  const alertCount = 
    (escalationSummary.unacknowledged || 0) + 
    (hipaaData?.summary?.unresolved_anomalies || 0) +
    triageQueue.filter(t => t.status === "waiting").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-slate-50"}`}>
      <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-6 max-w-7xl overflow-x-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6">
          <div>
            <h1 className={`text-xl sm:text-3xl font-bold flex items-center gap-2 sm:gap-3 ${isDark ? "text-white" : "text-gray-900"}`}>
              <div className="p-2 rounded-lg bg-gradient-to-br from-blue-600 to-purple-600">
                <Activity className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              Command Center
            </h1>
            <p className={`mt-1 text-sm sm:text-base ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              Real-time operations • Single source of truth
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {alertCount > 0 && (
              <Badge variant="destructive" className="animate-pulse px-2 sm:px-3 py-1">
                <Bell className="h-4 w-4 mr-1" />
                {alertCount} Alerts
              </Badge>
            )}
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={`h-4 w-4 sm:mr-2 ${refreshing ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button onClick={() => setShowNewReferralDialog(true)} data-testid="new-referral-btn" size="sm">
              <UserPlus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">New Referral</span>
            </Button>
          </div>
        </div>

        {/* Critical Alerts Banner */}
        {escalationSummary.critical > 0 && (
          <Card className="mb-6 border-red-500 border-2 bg-red-50 dark:bg-red-900/20">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-600 rounded-lg animate-pulse">
                    <AlertTriangle className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-red-800 dark:text-red-200">
                      {escalationSummary.critical} CRITICAL Escalation{escalationSummary.critical > 1 ? "s" : ""} Requiring Immediate Attention
                    </p>
                    <p className="text-sm text-red-600 dark:text-red-300">
                      Pain ≥7, Severe Dyspnea, or Life-Threatening Symptom Detected
                    </p>
                  </div>
                </div>
                <Button 
                  variant="destructive" 
                  onClick={() => setActiveTab("alerts")}
                >
                  Review Now
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <Card className={isDark ? "bg-gray-800 border-gray-700" : ""}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                <div>
                  <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>Active Escalations</p>
                  <p className={`text-xl font-bold ${isDark ? "text-white" : ""}`}>
                    {escalationSummary.total_active || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={isDark ? "bg-gray-800 border-gray-700" : ""}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Phone className="h-5 w-5 text-amber-500" />
                <div>
                  <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>Triage Queue</p>
                  <p className={`text-xl font-bold ${isDark ? "text-white" : ""}`}>
                    {triageQueue.length || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={isDark ? "bg-gray-800 border-gray-700" : ""}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-blue-500" />
                <div>
                  <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>New Referrals</p>
                  <p className={`text-xl font-bold ${isDark ? "text-white" : ""}`}>
                    {admissionStats?.pipeline_stats?.new || admissionStats?.new_referrals || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={isDark ? "bg-gray-800 border-gray-700" : ""}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Pill className="h-5 w-5 text-purple-500" />
                <div>
                  <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>Med Orders</p>
                  <p className={`text-xl font-bold ${isDark ? "text-white" : ""}`}>
                    {medicationQueue.length || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={isDark ? "bg-gray-800 border-gray-700" : ""}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-green-500" />
                <div>
                  <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>HIPAA Score</p>
                  <p className={`text-xl font-bold ${isDark ? "text-white" : ""}`}>
                    {hipaaData?.summary?.compliance_score || 95}%
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={isDark ? "bg-gray-800 border-gray-700" : ""}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-emerald-500" />
                <div>
                  <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>Today&apos;s Admits</p>
                  <p className={`text-xl font-bold ${isDark ? "text-white" : ""}`}>
                    {admissionStats?.admitted_today || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={`grid w-full grid-cols-2 sm:grid-cols-5 h-auto ${isDark ? "bg-gray-800" : ""}`}>
            <TabsTrigger value="alerts" className="relative py-3">
              <AlertTriangle className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Alerts</span>
              {alertCount > 0 && (
                <Badge variant="destructive" className="ml-1 sm:ml-2 h-5 w-5 p-0 text-xs flex items-center justify-center">
                  {alertCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="admissions" className="py-3">
              <UserPlus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Admissions</span>
            </TabsTrigger>
            <TabsTrigger value="triage" className="py-3">
              <Phone className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Call Triage</span>
            </TabsTrigger>
            <TabsTrigger value="medications" className="py-3">
              <Pill className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Medications</span>
            </TabsTrigger>
            <TabsTrigger value="compliance" className="py-3">
              <Shield className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Compliance</span>
            </TabsTrigger>
          </TabsList>

          {/* ALERTS TAB */}
          <TabsContent value="alerts" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Predictive Escalations */}
              <Card className={isDark ? "bg-gray-800 border-gray-700" : ""}>
                <CardHeader>
                  <CardTitle className={`flex items-center gap-2 ${isDark ? "text-white" : ""}`}>
                    <Brain className="h-5 w-5 text-red-500" />
                    Predictive Escalations
                  </CardTitle>
                  <CardDescription>
                    AI-detected clinical alerts requiring intervention
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {escalations.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                      <p className="font-medium">No Active Escalations</p>
                      <p className="text-sm">All patients within normal parameters</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-[300px]">
                      <div className="space-y-3">
                        {escalations.map((esc) => {
                          const priorityStyle = PRIORITY_COLORS[esc.priority] || PRIORITY_COLORS.moderate;
                          return (
                            <Card 
                              key={esc.id}
                              className={`cursor-pointer hover:shadow-md transition-shadow border-l-4 ${
                                esc.priority === "critical" ? "border-red-600" :
                                esc.priority === "high" ? "border-orange-500" :
                                "border-amber-500"
                              }`}
                              onClick={() => {
                                setSelectedEscalation(esc);
                                setShowEscalationDialog(true);
                              }}
                            >
                              <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <Badge className={`${priorityStyle.bg} ${priorityStyle.text} ${priorityStyle.pulse ? "animate-pulse" : ""}`}>
                                      {esc.priority?.toUpperCase()}
                                    </Badge>
                                    <div>
                                      <p className="font-medium">{esc.patient_name || "Patient"}</p>
                                      <p className="text-sm text-gray-500">{esc.trigger_description}</p>
                                    </div>
                                  </div>
                                  {esc.status === "triggered" && (
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAcknowledgeEscalation(esc.id);
                                      }}
                                    >
                                      Acknowledge
                                    </Button>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>

              {/* HIPAA Anomalies */}
              <Card className={isDark ? "bg-gray-800 border-gray-700" : ""}>
                <CardHeader>
                  <CardTitle className={`flex items-center gap-2 ${isDark ? "text-white" : ""}`}>
                    <Shield className="h-5 w-5 text-amber-500" />
                    HIPAA Activity Monitor
                  </CardTitle>
                  <CardDescription>
                    PHI access tracking and anomaly detection
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {hipaaData ? (
                    <div className="space-y-4">
                      {/* Compliance Score */}
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <div>
                          <p className="text-sm text-gray-500">Compliance Score</p>
                          <p className={`text-3xl font-bold ${
                            hipaaData.summary?.compliance_score >= 90 ? "text-green-600" :
                            hipaaData.summary?.compliance_score >= 70 ? "text-amber-600" :
                            "text-red-600"
                          }`}>
                            {hipaaData.summary?.compliance_score || 95}%
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-500">Today&apos;s PHI Access</p>
                          <p className="text-2xl font-bold">{hipaaData.summary?.phi_accesses_today || 0}</p>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                          <p className="text-xs text-gray-500">Active Sessions</p>
                          <p className="text-lg font-bold">{hipaaData.summary?.active_sessions || 0}</p>
                        </div>
                        <div className="text-center p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                          <p className="text-xs text-gray-500">Anomalies</p>
                          <p className="text-lg font-bold text-amber-600">
                            {hipaaData.summary?.unresolved_anomalies || 0}
                          </p>
                        </div>
                        <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                          <p className="text-xs text-gray-500">Total Access</p>
                          <p className="text-lg font-bold">{hipaaData.summary?.total_accesses_today || 0}</p>
                        </div>
                      </div>

                      {/* Top Accessed Patients */}
                      {hipaaData.top_accessed_patients?.length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-2">Most Accessed Patients</p>
                          <div className="space-y-2">
                            {hipaaData.top_accessed_patients.slice(0, 3).map((p, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm">
                                <span>{p.patient_name || p.patient_id}</span>
                                <Badge variant="outline">{p.access_count} views</Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>HIPAA monitoring data loading...</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* CMS Audit Readiness Score Widget */}
              <Card className={isDark ? "bg-gray-800 border-gray-700" : ""}>
                <CardHeader className="pb-2">
                  <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? "text-white" : ""}`}>
                    <ClipboardCheck className="h-5 w-5 text-blue-500" />
                    CMS Audit Readiness
                  </CardTitle>
                  <CardDescription>Real-time compliance score across 10 CMS audit categories</CardDescription>
                </CardHeader>
                <CardContent>
                  {cmsAuditScore ? (
                    <div className="space-y-4">
                      {/* Main Score Circle */}
                      <div className="flex items-center gap-6">
                        <div className="relative w-24 h-24">
                          <svg className="w-24 h-24 transform -rotate-90">
                            <circle cx="48" cy="48" r="40" stroke="currentColor" strokeWidth="8" fill="none" className="text-gray-200" />
                            <circle 
                              cx="48" cy="48" r="40" 
                              stroke="currentColor" 
                              strokeWidth="8" 
                              fill="none"
                              strokeDasharray={`${(cmsAuditScore.audit_readiness_score || 0) * 2.51} 251`}
                              className={
                                cmsAuditScore.audit_readiness_score >= 85 ? "text-green-500" :
                                cmsAuditScore.audit_readiness_score >= 70 ? "text-amber-500" :
                                cmsAuditScore.audit_readiness_score >= 50 ? "text-orange-500" :
                                "text-red-500"
                              }
                            />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className={`text-xl font-bold ${isDark ? "text-white" : ""}`}>
                              {Math.round(cmsAuditScore.audit_readiness_score)}%
                            </span>
                          </div>
                        </div>
                        <div className="flex-1">
                          <Badge className={`mb-2 ${
                            cmsAuditScore.readiness_level === "EXCELLENT" ? "bg-green-500" :
                            cmsAuditScore.readiness_level === "GOOD" ? "bg-blue-500" :
                            cmsAuditScore.readiness_level === "ACCEPTABLE" ? "bg-amber-500" :
                            cmsAuditScore.readiness_level === "AT RISK" ? "bg-orange-500" :
                            "bg-red-500"
                          }`}>
                            {cmsAuditScore.readiness_level}
                          </Badge>
                          <p className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                            Audit Risk: <span className="font-semibold">{cmsAuditScore.audit_risk}</span>
                          </p>
                        </div>
                      </div>
                      
                      {/* Category Breakdown */}
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {Object.entries(cmsAuditScore.score_breakdown || {}).slice(0, 6).map(([key, data]) => (
                          <div key={key} className={`flex items-center justify-between p-2 rounded ${isDark ? "bg-gray-700" : "bg-gray-50"}`}>
                            <span className="truncate">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                            <Badge variant={data.status === "compliant" ? "default" : data.status === "at_risk" ? "secondary" : "destructive"} className="text-xs">
                              {data.score}/{data.max_score}
                            </Badge>
                          </div>
                        ))}
                      </div>

                      {/* Findings Alert */}
                      {cmsAuditScore.findings?.length > 0 && (
                        <div className={`p-3 rounded-lg ${isDark ? "bg-red-900/30 border-red-700" : "bg-red-50 border-red-200"} border`}>
                          <p className="text-xs font-semibold text-red-600 mb-1">
                            <AlertTriangle className="h-3 w-3 inline mr-1" />
                            {cmsAuditScore.findings.length} Finding{cmsAuditScore.findings.length > 1 ? 's' : ''} Require Attention
                          </p>
                          <p className={`text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                            {cmsAuditScore.findings[0]?.finding?.slice(0, 80)}...
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-500">
                      <ClipboardCheck className="h-10 w-10 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Loading audit score...</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ADMISSIONS TAB - Single Source of Truth */}
          <TabsContent value="admissions" className="space-y-4">
            {/* Admission Pipeline Workflow */}
            <Card className={isDark ? "bg-gray-800 border-gray-700" : ""}>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${isDark ? "text-white" : ""}`}>
                  <UserPlus className="h-5 w-5 text-blue-500" />
                  Admission Pipeline - Single Source of Truth
                </CardTitle>
                <CardDescription>
                  All referrals (family, facility, physician, partner) flow through this unified pipeline
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Pipeline Stages */}
                <div className="grid grid-cols-7 gap-2 mb-6">
                  {[
                    { id: "new", label: "New", icon: Bell, count: admissionStats?.pipeline_stats?.new || 0 },
                    { id: "under_review", label: "Review", icon: Eye, count: admissionStats?.pipeline_stats?.under_review || 0 },
                    { id: "pending_documentation", label: "Pending Docs", icon: FileText, count: admissionStats?.pipeline_stats?.pending_documentation || 0 },
                    { id: "pending_cti", label: "Pending CTI", icon: Stethoscope, count: admissionStats?.pipeline_stats?.pending_cti || 0 },
                    { id: "approved", label: "Approved", icon: CheckCircle, count: admissionStats?.pipeline_stats?.approved || 0 },
                    { id: "scheduled_soc", label: "Scheduled SOC", icon: Calendar, count: admissionStats?.pipeline_stats?.scheduled_soc || 0 },
                    { id: "admitted", label: "Admitted", icon: Heart, count: admissionStats?.pipeline_stats?.admitted || admissionStats?.admitted_today || 0 }
                  ].map((stage, idx) => {
                    const StatusIcon = stage.icon;
                    const color = STATUS_COLORS[stage.id] || STATUS_COLORS.new;
                    return (
                      <div key={stage.id} className="relative">
                        <Card className={`text-center p-3 ${color.bg} ${color.border} border-t-4`}>
                          <StatusIcon className={`h-5 w-5 mx-auto mb-1 ${color.text}`} />
                          <p className="text-xs font-medium">{stage.label}</p>
                          <p className={`text-2xl font-bold ${color.text}`}>{stage.count}</p>
                        </Card>
                        {idx < 6 && (
                          <ChevronRight className="absolute -right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Conversion & Analytics */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <Card className="p-4 bg-blue-50 dark:bg-blue-900/20">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-blue-500" />
                      <div>
                        <p className="text-xs text-gray-500">Conversion Rate</p>
                        <p className="text-xl font-bold">{admissionStats?.conversion_rate || 78}%</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-4 bg-green-50 dark:bg-green-900/20">
                    <div className="flex items-center gap-2">
                      <Timer className="h-5 w-5 text-green-500" />
                      <div>
                        <p className="text-xs text-gray-500">Avg Days to Admit</p>
                        <p className="text-xl font-bold">{admissionStats?.avg_days_to_admit || 2.3}</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-4 bg-purple-50 dark:bg-purple-900/20">
                    <div className="flex items-center gap-2">
                      <Target className="h-5 w-5 text-purple-500" />
                      <div>
                        <p className="text-xs text-gray-500">This Week</p>
                        <p className="text-xl font-bold">{admissionStats?.weekly_admits || 12}</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-4 bg-amber-50 dark:bg-amber-900/20">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-amber-500" />
                      <div>
                        <p className="text-xs text-gray-500">Aging &gt;48h</p>
                        <p className="text-xl font-bold text-amber-600">{admissionStats?.aging_referrals || 0}</p>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Referral Sources */}
                <div className="grid grid-cols-6 gap-2 mb-4">
                  {[
                    { source: "hospital", label: "Hospital", count: admissionStats?.by_source?.hospital || 0 },
                    { source: "snf", label: "SNF/ALF", count: admissionStats?.by_source?.snf || 0 },
                    { source: "physician", label: "Physician", count: admissionStats?.by_source?.physician || 0 },
                    { source: "family", label: "Family/Self", count: admissionStats?.by_source?.family || 0 },
                    { source: "partner", label: "Partner", count: admissionStats?.by_source?.partner || 0 },
                    { source: "internal", label: "Internal", count: admissionStats?.by_source?.internal || 0 }
                  ].map((s) => {
                    const Icon = SOURCE_ICONS[s.source] || Building2;
                    return (
                      <div key={s.source} className="text-center p-2 bg-gray-50 dark:bg-gray-700/50 rounded">
                        <Icon className="h-4 w-4 mx-auto mb-1 text-gray-500" />
                        <p className="text-xs text-gray-500">{s.label}</p>
                        <p className="font-bold">{s.count}</p>
                      </div>
                    );
                  })}
                </div>

                <Button 
                  className="w-full" 
                  variant="outline"
                  onClick={() => navigate("/admissions-dashboard")}
                >
                  Open Full Admissions Dashboard
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CALL TRIAGE TAB */}
          <TabsContent value="triage" className="space-y-4">
            <Card className={isDark ? "bg-gray-800 border-gray-700" : ""}>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${isDark ? "text-white" : ""}`}>
                  <Phone className="h-5 w-5 text-amber-500" />
                  Call Triage Queue
                </CardTitle>
                <CardDescription>
                  Incoming calls with symptom classification and routing
                </CardDescription>
              </CardHeader>
              <CardContent>
                {triageQueue.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Phone className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="font-medium">No Active Calls</p>
                    <p className="text-sm">Triage queue is empty</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-3">
                      {triageQueue.map((call, idx) => (
                        <Card key={call.id || idx} className="border-l-4 border-amber-500">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <Badge variant={call.urgency === "urgent" ? "destructive" : "outline"}>
                                    {call.urgency || "routine"}
                                  </Badge>
                                  <p className="font-medium">{call.patient_name || call.caller_name || "Unknown"}</p>
                                </div>
                                <p className="text-sm text-gray-500 mt-1">
                                  {call.reason || call.call_reason || "Incoming call"}
                                </p>
                                {call.symptom_classification && (
                                  <Badge variant="outline" className="mt-2">
                                    {call.symptom_classification}
                                  </Badge>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-gray-500">Wait Time</p>
                                <p className="font-bold">{call.wait_time || "< 1 min"}</p>
                                <Button size="sm" className="mt-2">
                                  Take Call
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* MEDICATIONS TAB */}
          <TabsContent value="medications" className="space-y-4">
            <Card className={isDark ? "bg-gray-800 border-gray-700" : ""}>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${isDark ? "text-white" : ""}`}>
                  <Pill className="h-5 w-5 text-purple-500" />
                  Medication Orders Pending Review
                </CardTitle>
                <CardDescription>
                  AI-generated recommendations awaiting physician approval
                </CardDescription>
              </CardHeader>
              <CardContent>
                {medicationQueue.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                    <p className="font-medium">All Caught Up!</p>
                    <p className="text-sm">No pending medication orders</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {medicationQueue.slice(0, 5).map((bundle) => (
                      <Card 
                        key={bundle.id}
                        className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-purple-500"
                        onClick={() => navigate("/medication-workflow")}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{bundle.patient_name}</p>
                              <p className="text-sm text-gray-500">
                                {bundle.recommendations?.length || 0} medications • {bundle.trigger_type?.replace(/_/g, " ")}
                              </p>
                            </div>
                            <Badge className={
                              bundle.confidence_indicator === "high" ? "bg-green-500" :
                              bundle.confidence_indicator === "medium" ? "bg-amber-500" :
                              "bg-red-500"
                            }>
                              {bundle.confidence_indicator?.toUpperCase() || "MEDIUM"}
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    {medicationQueue.length > 5 && (
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={() => navigate("/medication-workflow")}
                      >
                        View All {medicationQueue.length} Orders
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* COMPLIANCE TAB */}
          <TabsContent value="compliance" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* HIPAA Details */}
              <Card className={isDark ? "bg-gray-800 border-gray-700" : ""}>
                <CardHeader>
                  <CardTitle className={`flex items-center gap-2 ${isDark ? "text-white" : ""}`}>
                    <Shield className="h-5 w-5 text-green-500" />
                    HIPAA Compliance Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-center">
                      <div className="relative w-32 h-32">
                        <svg className="w-32 h-32 transform -rotate-90">
                          <circle
                            cx="64"
                            cy="64"
                            r="56"
                            stroke="currentColor"
                            strokeWidth="8"
                            fill="none"
                            className="text-gray-200"
                          />
                          <circle
                            cx="64"
                            cy="64"
                            r="56"
                            stroke="currentColor"
                            strokeWidth="8"
                            fill="none"
                            strokeDasharray={`${(hipaaData?.summary?.compliance_score || 95) * 3.52} 352`}
                            className={
                              (hipaaData?.summary?.compliance_score || 95) >= 90 ? "text-green-500" :
                              (hipaaData?.summary?.compliance_score || 95) >= 70 ? "text-amber-500" :
                              "text-red-500"
                            }
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-3xl font-bold">{hipaaData?.summary?.compliance_score || 95}%</span>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <p className="text-xs text-gray-500">Access Logs Today</p>
                        <p className="text-xl font-bold">{hipaaData?.summary?.total_accesses_today || 0}</p>
                      </div>
                      <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                        <p className="text-xs text-gray-500">Anomalies</p>
                        <p className="text-xl font-bold text-amber-600">{hipaaData?.summary?.unresolved_anomalies || 0}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Activity by Role */}
              <Card className={isDark ? "bg-gray-800 border-gray-700" : ""}>
                <CardHeader>
                  <CardTitle className={`flex items-center gap-2 ${isDark ? "text-white" : ""}`}>
                    <Users className="h-5 w-5 text-blue-500" />
                    Activity by Role
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {hipaaData?.activity_by_role?.length > 0 ? (
                    <div className="space-y-3">
                      {hipaaData.activity_by_role.map((role, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <span className="capitalize">{role.role || "Unknown"}</span>
                          <div className="flex items-center gap-2">
                            <Progress value={(role.count / (hipaaData.summary?.total_accesses_today || 1)) * 100} className="w-24" />
                            <Badge variant="outline">{role.count}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No activity data available</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* New Referral Dialog */}
      <Dialog open={showNewReferralDialog} onOpenChange={setShowNewReferralDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New Referral - Admission Pipeline</DialogTitle>
            <DialogDescription>
              All referrals enter through this unified intake form
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Referral Source *</label>
              <Select 
                value={newReferral.source} 
                onValueChange={(v) => setNewReferral({...newReferral, source: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hospital">Hospital</SelectItem>
                  <SelectItem value="snf">SNF (Skilled Nursing)</SelectItem>
                  <SelectItem value="alf">ALF (Assisted Living)</SelectItem>
                  <SelectItem value="physician">Physician</SelectItem>
                  <SelectItem value="family">Family / Self</SelectItem>
                  <SelectItem value="partner">Partner Organization</SelectItem>
                  <SelectItem value="internal">Internal Conversion</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Source Name</label>
              <Input 
                value={newReferral.source_name}
                onChange={(e) => setNewReferral({...newReferral, source_name: e.target.value})}
                placeholder="Hospital/Facility/Physician name"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Patient Name *</label>
              <Input 
                value={newReferral.patient_name}
                onChange={(e) => setNewReferral({...newReferral, patient_name: e.target.value})}
                placeholder="First Last"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Date of Birth</label>
              <Input 
                type="date"
                value={newReferral.patient_dob}
                onChange={(e) => setNewReferral({...newReferral, patient_dob: e.target.value})}
              />
            </div>

            <div>
              <label className="text-sm font-medium">Primary Diagnosis *</label>
              <Input 
                value={newReferral.primary_diagnosis}
                onChange={(e) => setNewReferral({...newReferral, primary_diagnosis: e.target.value})}
                placeholder="e.g., Terminal Cancer, CHF, COPD"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Urgency</label>
              <Select 
                value={newReferral.urgency} 
                onValueChange={(v) => setNewReferral({...newReferral, urgency: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="routine">Routine</SelectItem>
                  <SelectItem value="urgent">Urgent (24-48h)</SelectItem>
                  <SelectItem value="gip">GIP (General Inpatient)</SelectItem>
                  <SelectItem value="crisis">Crisis</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Care Location</label>
              <Select 
                value={newReferral.care_location} 
                onValueChange={(v) => setNewReferral({...newReferral, care_location: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="home">Home</SelectItem>
                  <SelectItem value="facility">Facility</SelectItem>
                  <SelectItem value="hospice_house">Hospice House</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Payer Type</label>
              <Select 
                value={newReferral.payer_type} 
                onValueChange={(v) => setNewReferral({...newReferral, payer_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="medicare">Medicare</SelectItem>
                  <SelectItem value="medicaid">Medicaid</SelectItem>
                  <SelectItem value="commercial">Commercial Insurance</SelectItem>
                  <SelectItem value="charity">Charity/Self-Pay</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Referring Physician</label>
              <Input 
                value={newReferral.referring_physician}
                onChange={(e) => setNewReferral({...newReferral, referring_physician: e.target.value})}
                placeholder="Dr. Name"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Contact Phone</label>
              <Input 
                value={newReferral.contact_phone}
                onChange={(e) => setNewReferral({...newReferral, contact_phone: e.target.value})}
                placeholder="(555) 555-5555"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewReferralDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmitReferral}>
              <UserPlus className="h-4 w-4 mr-2" />
              Submit to Pipeline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Escalation Detail Dialog */}
      <Dialog open={showEscalationDialog} onOpenChange={setShowEscalationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Escalation Details</DialogTitle>
          </DialogHeader>

          {selectedEscalation && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge className={
                  selectedEscalation.priority === "critical" ? "bg-red-600" :
                  selectedEscalation.priority === "high" ? "bg-orange-500" :
                  "bg-amber-500"
                }>
                  {selectedEscalation.priority?.toUpperCase()}
                </Badge>
                <span className="font-medium">{selectedEscalation.trigger_type?.replace(/_/g, " ")}</span>
              </div>

              <Card className="bg-gray-50 dark:bg-gray-800">
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Patient</p>
                      <p className="font-medium">{selectedEscalation.patient_name || "Unknown"}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Status</p>
                      <p className="font-medium capitalize">{selectedEscalation.status}</p>
                    </div>
                    {selectedEscalation.symptom_data?.pain_score && (
                      <div>
                        <p className="text-gray-500">Pain Score</p>
                        <p className="font-medium">{selectedEscalation.symptom_data.pain_score}/10</p>
                      </div>
                    )}
                    {selectedEscalation.symptom_data?.location && (
                      <div>
                        <p className="text-gray-500">Location</p>
                        <p className="font-medium">{selectedEscalation.symptom_data.location}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <p className="text-sm text-gray-600">{selectedEscalation.trigger_description}</p>

              {selectedEscalation.audit_trail?.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Audit Trail</p>
                  <div className="space-y-1 text-xs text-gray-500">
                    {selectedEscalation.audit_trail.map((entry, idx) => (
                      <p key={idx}>
                        {entry.timestamp?.split("T")[1]?.slice(0, 5)} - {entry.action}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEscalationDialog(false)}>
              Close
            </Button>
            {selectedEscalation?.status === "triggered" && (
              <Button onClick={() => {
                handleAcknowledgeEscalation(selectedEscalation.id);
                setShowEscalationDialog(false);
              }}>
                Acknowledge
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
